import { Box3, Camera, Frustum, Matrix4, Object3D, Vector3 } from 'three';

const MEGABYTE_BYTES = 1024 ** 2;
const GIGABYTE_BYTES = 1024 ** 3;

/**
 * LOD settings depend on how heavy the tiles are. Our standard tiled models have small tiles (~90 kB) and work well
 * with the library defaults, while manually uploaded tilesets can have tiles of tens to hundreds of MB that need a
 * much more conservative setup.
 *
 * Tilesets start in the probe profile that only loads the root content, and pick the heavy profile if that tile is
 * above this size. The size is the estimated in-memory size with decoded textures, so a ~90 kB tile with a 1024²
 * texture is already ~5.6 MB. The heavy tiles we've seen are 64 MB and up.
 */
export const HEAVY_TILE_BYTES = 32 * MEGABYTE_BYTES;

/**
 * Heavy profile: a tile refines once less than this fraction of its content is in view. While most of a tile fits on
 * screen there is no need to load its children.
 */
export const HEAVY_REFINE_VISIBLE_FRACTION = 0.6;

/** Heavy profile: a tile is not loaded yet while less than this fraction of it is in view. */
export const HEAVY_MIN_LOAD_VISIBLE_FRACTION = 0.1;

export type LodProfile = 'probe' | 'light' | 'heavy';

export interface LodSettings {
  /** Select the LOD by the visible fraction of the tile content, see applyScreenFractionError. */
  useScreenFraction: boolean;
  /** Tiles that haven't loaded yet are not loaded while less than this fraction of them is in view. */
  minLoadVisibleFraction: number;
  errorTarget: number;
  optimizedLoadStrategy: boolean;
  loadSiblings: boolean;
  lruCache: { minBytesSize: number; maxBytesSize: number };
  maxDownloadJobs: number;
  maxParseJobs: number;
  /** Options for the UnloadTilesPlugin, which is only used when defined. */
  unloadTiles?: { delay: number; bytesTarget: number };
}

/**
 * Library defaults, matching what the standard tiled models were tuned for: refine by the screen-space error to the
 * bounding volumes, load all siblings of a refined tile and keep the parents around as a background while the
 * children load.
 */
const LIGHT_SETTINGS: LodSettings = {
  useScreenFraction: false,
  minLoadVisibleFraction: 0,
  errorTarget: 16,
  optimizedLoadStrategy: false,
  loadSiblings: true,
  lruCache: { minBytesSize: 0.3 * GIGABYTE_BYTES, maxBytesSize: 0.4 * GIGABYTE_BYTES },
  maxDownloadJobs: 25,
  maxParseJobs: 5,
};

/**
 * Heavy tiles (tens to hundreds of MB each, mostly textures up to 8k):
 * - useScreenFraction: refine a tile once less than HEAVY_REFINE_VISIBLE_FRACTION of it is in view, and don't load
 *   tiles while less than HEAVY_MIN_LOAD_VISIBLE_FRACTION of them is in view, see applyScreenFractionError.
 * - optimizedLoadStrategy + loadSiblings false: only load the children that are in the camera frustum. The default
 *   strategy loads every sibling of a refined tile, including ones outside the view. Coarser levels still load first
 *   since tiles don't refine before they have loaded, so there is a parent to fall back on.
 * - lruCache: unused tiles are evicted down to minBytesSize. maxBytesSize stays unlimited because hitting it evicts
 *   tiles that are still parsing, and with REPLACE refinement the parent then waits forever for that child.
 * - download/parse queues: one tile at a time, closest to the screen center first. When the camera stops, what is in
 *   the middle of the screen loads first, and tiles queued for an earlier view are overtaken.
 * - unloadTiles: disposes GPU resources of tiles that leave the view, but only while the GPU usage is above
 *   bytesTarget. Without a target every tile leaving the view was unloaded, and its textures had to be re-uploaded as
 *   soon as it came back into view, which made looking around very laggy.
 */
const HEAVY_SETTINGS: LodSettings = {
  useScreenFraction: true,
  minLoadVisibleFraction: HEAVY_MIN_LOAD_VISIBLE_FRACTION,
  errorTarget: 1 - HEAVY_REFINE_VISIBLE_FRACTION,
  optimizedLoadStrategy: true,
  loadSiblings: false,
  lruCache: { minBytesSize: 1 * GIGABYTE_BYTES, maxBytesSize: Infinity },
  maxDownloadJobs: 1,
  maxParseJobs: 1,
  unloadTiles: { delay: 1000, bytesTarget: 0.5 * GIGABYTE_BYTES },
};

/** Light settings that don't refine past the root, until its content has loaded and the profile is known. */
const PROBE_SETTINGS: LodSettings = { ...LIGHT_SETTINGS, errorTarget: Infinity };

export function getLodSettings(profile: LodProfile): LodSettings {
  switch (profile) {
    case 'probe':
      return PROBE_SETTINGS;
    case 'light':
      return LIGHT_SETTINGS;
    case 'heavy':
      return HEAVY_SETTINGS;
  }
}

/** Picks the profile from the estimated in-memory size of the first loaded tile. */
export function selectLodProfile(tileBytes: number): Exclude<LodProfile, 'probe'> {
  return tileBytes > HEAVY_TILE_BYTES ? 'heavy' : 'light';
}

/** The parts of a 3d-tiles-renderer tile used for the LOD selection. */
export interface LodTile {
  parent?: LodTile | null;
  children?: LodTile[];
  internal?: { hasRenderableContent?: boolean };
  engineData?: { boundingVolume?: { getAABB(target: Box3): void } };
}

/** The view error target filled in by 3d-tiles-renderer's calculateTileViewError. */
export interface ViewErrorTarget {
  inView: boolean;
  error: number;
  distanceFromCamera: number;
}

export interface ScreenFractionContext {
  /** Content bounds in the tiles group frame for every tile that has been loaded. */
  contentBounds: Map<LodTile, Box3>;
  cameras: Camera[];
  /** The tiles group, the content bounds are in its frame. */
  group: Object3D;
  minLoadVisibleFraction: number;
}

/** Sample points per horizontal axis / vertically used to estimate the visible fraction of a tile's content. */
const VISIBILITY_SAMPLES_XY = 6;
const VISIBILITY_SAMPLES_Z = 2;

const tempFrustum = new Frustum();
const tempClipMatrix = new Matrix4();
const tempViewMatrix = new Matrix4();
const tempPoint = new Vector3();
const tempCenter = new Vector3();
const tempHalfSize = new Vector3();
const tempBox = new Box3();

/**
 * Overrides the library's view error of a tile (screen-space error to its bounding volume) with one based on how much
 * of the tile's content is in view. Applied on top of the library's calculation, which still decides whether the
 * tile is in the frustum at all.
 *
 * - The visible fraction is estimated by sampling points in the tile's content bounds and checking how many are inside
 *   the camera frustum. Rotating the camera only changes what is in view, unlike the projected screen area (which
 *   blows up for the near edge of tiles in oblique views) or the distance to the content (which changes when orbiting
 *   around a pivot).
 * - A loaded tile's error is 1 - visible fraction, so with errorTarget = 1 - fraction it refines once less than that
 *   fraction of it is in view.
 * - Tiles whose content has never been loaded report no error, so they never refine before they have loaded once.
 *   This makes the refinement progressive (one level at a time) instead of the whole chain down to the finest level
 *   being requested at once. They are treated as out of view while less than minLoadVisibleFraction of their
 *   estimated content bounds is in view.
 * - distanceFromCamera is set to the distance from the screen center, which the optimized load strategy uses as the
 *   load priority, so tiles in the middle of the screen load first.
 *
 * The bounding volumes are not used for the size, since in our tilesets they are far larger than the flat content
 * inside them (e.g. a ~284 m tall sphere around ~45 m tall content).
 */
export function applyScreenFractionError(
  tile: LodTile,
  target: ViewErrorTarget,
  context: ScreenFractionContext,
) {
  // Tiles without renderable content (e.g. empty grouping tiles or external tilesets) never get any content bounds,
  // so they keep the library's error.
  if (!tile.internal?.hasRenderableContent || context.cameras.length === 0) {
    return;
  }

  const contentBox = context.contentBounds.get(tile);

  // Content without any geometry bounds, keep the library's error.
  if (contentBox?.isEmpty()) {
    return;
  }

  const box = contentBox ?? estimateContentBounds(tile, context.contentBounds, tempBox);

  // Nothing to go on yet (the root before it has loaded), just load it.
  if (!box) {
    target.error = 0;
    return;
  }

  let visibleFraction = 0;
  let screenCenterOffset = Infinity;
  for (const camera of context.cameras) {
    tempViewMatrix.multiplyMatrices(camera.matrixWorldInverse, context.group.matrixWorld);
    tempClipMatrix.multiplyMatrices(camera.projectionMatrix, tempViewMatrix);
    tempFrustum.setFromProjectionMatrix(tempClipMatrix);

    visibleFraction = Math.max(visibleFraction, getVisibleFraction(box, tempFrustum));
    screenCenterOffset = Math.min(
      screenCenterOffset,
      getScreenCenterOffset(box, tempViewMatrix, tempClipMatrix),
    );
  }

  target.distanceFromCamera = screenCenterOffset;

  if (!contentBox) {
    target.error = 0;
    if (visibleFraction < context.minLoadVisibleFraction) {
      target.inView = false;
    }
    return;
  }

  target.error = 1 - visibleFraction;
}

/** Fraction (0-1) of sample points in the box that are inside the frustum. */
export function getVisibleFraction(box: Box3, frustum: Frustum) {
  let inside = 0;
  for (let x = 0; x < VISIBILITY_SAMPLES_XY; x++) {
    for (let y = 0; y < VISIBILITY_SAMPLES_XY; y++) {
      for (let z = 0; z < VISIBILITY_SAMPLES_Z; z++) {
        tempPoint.set(
          box.min.x + ((x + 0.5) / VISIBILITY_SAMPLES_XY) * (box.max.x - box.min.x),
          box.min.y + ((y + 0.5) / VISIBILITY_SAMPLES_XY) * (box.max.y - box.min.y),
          box.min.z + ((z + 0.5) / VISIBILITY_SAMPLES_Z) * (box.max.z - box.min.z),
        );
        if (frustum.containsPoint(tempPoint)) {
          inside++;
        }
      }
    }
  }

  return inside / (VISIBILITY_SAMPLES_XY * VISIBILITY_SAMPLES_XY * VISIBILITY_SAMPLES_Z);
}

/** Distance of the box center from the screen center in normalized device coordinates, Infinity if behind. */
function getScreenCenterOffset(box: Box3, viewMatrix: Matrix4, clipMatrix: Matrix4) {
  // The camera looks down -z.
  if (box.getCenter(tempPoint).applyMatrix4(viewMatrix).z >= 0) {
    return Infinity;
  }

  box.getCenter(tempPoint).applyMatrix4(clipMatrix);
  return Math.hypot(tempPoint.x, tempPoint.y);
}

/**
 * Estimates the content bounds of a tile that hasn't loaded yet: centered on its bounding volume (the bounding volumes
 * are oversized but roughly centered), with the horizontal size of the closest loaded ancestor's content split
 * between its siblings, and clipped to that content.
 */
export function estimateContentBounds(
  tile: LodTile,
  contentBounds: Map<LodTile, Box3>,
  target: Box3,
) {
  let ancestor = tile.parent;
  while (ancestor && !contentBounds.has(ancestor)) {
    ancestor = ancestor.parent;
  }
  const ancestorBox = ancestor ? contentBounds.get(ancestor) : undefined;
  if (!ancestorBox || ancestorBox.isEmpty()) {
    return undefined;
  }

  target.makeEmpty();
  tile.engineData?.boundingVolume?.getAABB(target);
  if (target.isEmpty()) {
    return undefined;
  }
  target.getCenter(tempCenter);

  const siblings = Math.max(tile.parent?.children?.length ?? 1, 1);
  ancestorBox.getSize(tempHalfSize).multiplyScalar(0.5);
  tempHalfSize.x /= Math.sqrt(siblings);
  tempHalfSize.y /= Math.sqrt(siblings);

  target.min.set(tempCenter.x - tempHalfSize.x, tempCenter.y - tempHalfSize.y, ancestorBox.min.z);
  target.max.set(tempCenter.x + tempHalfSize.x, tempCenter.y + tempHalfSize.y, ancestorBox.max.z);
  target.intersect(ancestorBox);
  return target.isEmpty() ? undefined : target;
}
